import { ParticipationStatus } from '../types/';
import { EventStatus, UserStatusApiResponse } from '../types/api.types';

const getFinishedEventStatus = (eventStatus: EventStatus): ParticipationStatus | null => {
  switch (eventStatus) {
    case EventStatus.FINISHED:
      return 'awaiting_distribution';
    case EventStatus.REWARDS_DISTRIBUTED:
      return 'reward_distributed';
    default:
      return null;
  }
};

export const getParticipationStatus = (
  eventStatus: EventStatus | undefined,
  registrationError: string | null,
  userStatus: UserStatusApiResponse | undefined,
  walletAddress: string | null
): ParticipationStatus => {
  // No event status data - fallback to register
  if (!eventStatus) return 'register';

  // If event is finished or rewards distributed, registration error doesn't matter
  // because user can't register anyway - show the actual event state
  const finishedStatus = getFinishedEventStatus(eventStatus);
  if (finishedStatus) {
    return mapUserStatusToParticipationStatus(userStatus, eventStatus, walletAddress);
  }

  // If there's a registration error and event is still active, show not eligible
  if (registrationError) return 'not_eligible';

  // Default - map based on user status
  return mapUserStatusToParticipationStatus(userStatus, eventStatus, walletAddress);
};

export const mapUserStatusToParticipationStatus = (
  userStatus: UserStatusApiResponse | undefined,
  eventStatus: EventStatus,
  walletAddress: string | null
): ParticipationStatus => {
  // User not logged in - show register button
  if (!walletAddress) {
    return 'register';
  }

  // No user status data available - fallback to basic logic
  if (!userStatus) {
    const finishedStatus = getFinishedEventStatus(eventStatus);
    if (finishedStatus) return finishedStatus;
    if (eventStatus === EventStatus.ONGOING) return 'register';
    return 'not_eligible';
  }

  // Map based on registration status
  switch (userStatus.status) {
    case 'REGISTERED': {
      // User is registered - check event status to determine if still participating or awaiting distribution
      const finishedStatus = getFinishedEventStatus(eventStatus);
      return finishedStatus ?? 'participating';
    }

    case 'AWAITING_DISTRIBUTION':
      return 'awaiting_distribution';

    case 'DISTRIBUTED':
      return 'reward_distributed';

    case 'NOT_REGISTERED':
    default: {
      // If event is finished or rewards distributed, show that status
      // (eligibility doesn't matter anymore - registration is closed)
      const finishedStatus = getFinishedEventStatus(eventStatus);
      if (finishedStatus) return finishedStatus;
      // User not registered - check eligibility
      if (userStatus.eligibility.status === 'NOT_ELIGIBLE') {
        return 'not_eligible';
      }
      // Eligible but not registered - show register button
      return 'register';
    }
  }
};
